import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { http } from '../../../api';

type Status =
  | { configured: false; reason?: string }
  | { configured: true; connected: false }
  | { configured: true; connected: true; org_name: string | null; access_token_expires_at: string | null };

type SyncRow = {
  entity_kind: string;
  entity_id: number | string;
  xero_id: string | null;
  status: 'pending' | 'synced' | 'failed';
  last_synced_at: string | null;
  last_error: string | null;
  attempts: number;
};

// Xero integration page. OAuth handshake opens in a new tab; Xero redirects
// back to /xero/callback which closes the loop and bounces the user back here
// with ?connected=1. Recent sync state is listed so failed pushes surface
// immediately without having to tail the API logs.
export default function XeroSettings() {
  const [status, setStatus] = useState<Status | null>(null);
  const [syncRows, setSyncRows] = useState<SyncRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  // Read ?connected=1 / ?error=... from the callback bounce and clear them.
  useEffect(() => {
    if (params.get('connected') === '1') {
      setToast(`Connected to ${params.get('org') || 'Xero'}.`);
      params.delete('connected'); params.delete('org'); setParams(params, { replace: true });
    } else if (params.get('error')) {
      setError(`Xero callback: ${params.get('error')}`);
      params.delete('error'); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const load = async () => {
    try {
      const s = await http.get<Status>('/xero/status');
      setStatus(s);
      if ((s as { connected?: boolean }).connected) {
        const r = await http.get<{ rows: SyncRow[] }>('/xero/sync/state');
        setSyncRows(r.rows);
      } else {
        setSyncRows([]);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const connect = async () => {
    setBusy('connect'); setError(null);
    try {
      const r = await http.post<{ url: string }>('/xero/auth-url');
      // Open in a new tab so the user's current session stays intact.
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Xero? Future invoice / payment syncs will be skipped until you reconnect.')) return;
    setBusy('disconnect'); setError(null);
    try {
      await http.post('/xero/disconnect');
      setToast('Xero disconnected.');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <header>
        <h1>Xero</h1>
        <p className="muted">
          Push issued invoices and payments into Xero automatically. Xero is the only accounting integration
          supported; direct bank-feed APIs are out of scope.
        </p>
      </header>

      {toast && (
        <div className="card" style={{ borderColor: 'var(--color-accent)', background: 'rgba(201,163,74,0.08)' }}>
          {toast}
        </div>
      )}
      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      {/* --- Status panel --- */}
      <div className="card stack">
        {status === null ? (
          <div className="muted">Loading…</div>
        ) : !status.configured ? (
          <>
            <b>Xero isn't configured on this deployment.</b>
            <p className="muted" style={{ fontSize: 13 }}>
              {status.reason || 'Set XERO_CLIENT_ID and XERO_CLIENT_SECRET on the api function, then redeploy.'}
            </p>
          </>
        ) : !status.connected ? (
          <div className="row">
            <div className="grow">
              <b>Not connected.</b>
              <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
                Connect to let Litigate push invoices and payments into your Xero organisation.
                You'll be asked to sign in to Xero and grant access.
              </p>
            </div>
            <button className="primary" onClick={connect} disabled={busy === 'connect'}>
              {busy === 'connect' ? '…' : 'Connect Xero'}
            </button>
          </div>
        ) : (
          <div className="row">
            <div className="grow">
              <b>Connected to {status.org_name || 'Xero'}.</b>
              <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
                Issued invoices and recorded payments are synced to this Xero organisation automatically.
                Access token refreshes in the background.
              </p>
            </div>
            <button onClick={disconnect} disabled={busy === 'disconnect'}>
              {busy === 'disconnect' ? '…' : 'Disconnect'}
            </button>
          </div>
        )}
      </div>

      {/* --- Recent sync state --- */}
      {status && (status as { connected?: boolean }).connected && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
          }}>
            <h3 style={{ margin: 0 }}>Recent sync activity</h3>
            <button onClick={load}>Refresh</button>
          </div>
          {syncRows.length === 0 ? (
            <div className="muted" style={{ padding: 18 }}>
              No sync activity yet. Issue an invoice or record a payment to see entries here.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>#</th>
                  <th>Xero ID</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Last synced</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {syncRows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ textTransform: 'capitalize' }}>{r.entity_kind}</td>
                    <td className="muted">#{String(r.entity_id)}</td>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>
                      {r.xero_id ? `${r.xero_id.slice(0, 8)}…` : '—'}
                    </td>
                    <td>
                      <span className={`pill ${r.status === 'synced' ? 'success' : r.status === 'failed' ? 'danger' : ''}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{r.attempts}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {r.last_synced_at ? String(r.last_synced_at).slice(0, 16).replace('T', ' ') : '—'}
                    </td>
                    <td className="muted" style={{ fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.last_error || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
